import { useCallback, useState } from 'react';
import { Dialog } from '@mui/material';
import { Api, Domain, Hooks } from '@3nickels/data-modules';
import { useService } from '@aesop-fables/containr-react';
import { PageContentWithBackground } from '../../../layout/PageContentWithBackground';
import { getLegalDocByDescription } from '../Footnote';
import { formatDecimalDollars } from '../../../../helpers/utilityFunctions';
import { handleUpgradeCheckout, handleResubscribe } from '../utils';
import DesktopUpsellContent from './DesktopUpsellContent';
import { useMessage } from '../../../../hooks/useMessage';
import { useLoading } from '../../../../hooks/useLoading';
import useWindowDimensions from '../../../../hooks/useWindowDimensions';
import { useDeviceType } from '../../../../hooks/useDeviceType';
import MobileUpsellContent from './mobile/MobileUpsellContent';
import ResubscribeModal from '../ResubscribeModal';

interface FreemiumUpsellScreenProps {
  open: boolean;
  handleClose: () => void;
  mobile: boolean;
}

const FreemiumUpsellScreen: React.FC<FreemiumUpsellScreenProps> = ({
  open,
  handleClose,
  mobile,
}) => {
  const commands = Hooks.useCommands();
  const termsApi = useService<Api.TermsAndConditionsApi>(Api.ApiKeys.TermsAndConditions);
  const legalDocs = Hooks.useTermsAndConditions();
  const { showMessage } = useMessage();
  const { setLoading } = useLoading();
  const [resubscribeModalOpen, setResubscribeModalOpen] = useState(false);

  const { addOnPricebook, addOnSubscription } = Hooks.useFreemiumInfo();
  const { threeNickelsProducts } = Hooks.useAvailableProducts();
  const addOnActive = Boolean(addOnSubscription?.mappedStatus?.active);
  const needsResubscribe = addOnSubscription && !addOnActive;
  const advicePrice = formatDecimalDollars((addOnPricebook?.prices[0].price ?? 0) / 100);
  const { height } = useWindowDimensions();
  const { isDesktop } = useDeviceType();

  const onUpgradeToPremium = useCallback(async () => {
    const pricebookId = addOnPricebook?.pricebook.id;
    if (!pricebookId) throw new Error('No pricebook id found');

    if (!needsResubscribe) {
      await handleUpgradeCheckout(commands, pricebookId, setLoading, showMessage);
      return;
    }

    if (addOnSubscription?.origin === Domain.PaymentOriginEnum.STRIPE) {
      setResubscribeModalOpen(true);
      return;
    }

    handleClose();
    await handleResubscribe(commands, pricebookId, setLoading, showMessage);
  }, [addOnPricebook?.pricebook.id, needsResubscribe, addOnSubscription?.origin, handleClose]);

  const openDoc = async (description: 'Terms of Service' | 'Privacy Policy') => {
    const url = await getLegalDocByDescription(termsApi, legalDocs, description);
    window.open(url);
  };

  return (
    <Dialog fullScreen open={open} onClose={handleClose}>
      {addOnSubscription && (
        <ResubscribeModal
          modalOpen={resubscribeModalOpen}
          setModalOpen={setResubscribeModalOpen}
          subscription={addOnSubscription}
          threeNickelsProducts={threeNickelsProducts}
          onResubscribe={handleClose}
        />
      )}
      <PageContentWithBackground
        centeredContent
        sx={{
          height: isDesktop ? undefined : height,
        }}>
        {mobile ? (
          <MobileUpsellContent
            advicePrice={advicePrice}
            openDoc={openDoc}
            onUpgradeToPremium={onUpgradeToPremium}
            handleClose={handleClose}
          />
        ) : (
          <DesktopUpsellContent
            advicePrice={advicePrice}
            openDoc={openDoc}
            onUpgradeToPremium={onUpgradeToPremium}
            handleClose={handleClose}
          />
        )}
      </PageContentWithBackground>
    </Dialog>
  );
};

export default FreemiumUpsellScreen;
